/**
 * OP Sync Audit View — Three-step flow
 *
 * Step 1 (Configure): Set lookback window and max records. Start detection
 *                      or run all. Browse and load past audit runs.
 * Step 2 (Select):    Review detected changes, pick records, dispatch for audit.
 * Step 3 (Results):   Two views — "By Field" (aggregate) and "By Record" (detail).
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, Loader2, RotateCcw } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  dispatchOPSyncAuditRun,
  getOPSyncAuditRun,
  getOPSyncAuditRunFieldSummary,
  getOPSyncAuditRunResults,
  listOPSyncAuditRuns,
  startOPSyncAuditDetectOnly,
  startOPSyncAuditWindow,
} from '@/lib/api';
import {
  type AuditStep,
  type LookbackDays,
  LOOKBACK_OPTIONS,
  buildSinceDate,
  formatDateTime,
  Stat,
} from './shared';
import {
  AuditRecordReviewWorkspace,
  DetectedChangesTable,
  AuditRunItemsTable,
  AuditRunsTable,
  FieldMismatchTable,
} from './DataTables';

/* ---- Breadcrumb (lightweight step indicator) ---- */

function StepBreadcrumb({
  current,
  reachable,
  onStepClick,
}: {
  current: AuditStep;
  reachable: Set<AuditStep>;
  onStepClick: (step: AuditStep) => void;
}) {
  const steps: { key: AuditStep; label: string }[] = [
    { key: 'configure', label: 'Configure' },
    { key: 'select', label: 'Select' },
    { key: 'results', label: 'Results' },
  ];

  return (
    <nav
      className="flex items-center gap-1.5 text-xs"
      aria-label="Audit steps"
    >
      {steps.map((s, i) => {
        const isActive = s.key === current;
        const isClickable = reachable.has(s.key) && !isActive;

        return (
          <span key={s.key} className="flex items-center gap-1.5">
            {i > 0 && (
              <ChevronRight className="h-3 w-3 text-muted-foreground/40" />
            )}
            {isClickable ? (
              <button
                type="button"
                onClick={() => onStepClick(s.key)}
                className="text-muted-foreground transition-colors hover:text-foreground hover:underline"
              >
                {s.label}
              </button>
            ) : (
              <span
                className={
                  isActive
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground/40'
                }
              >
                {s.label}
              </span>
            )}
          </span>
        );
      })}
    </nav>
  );
}

/* ---- Main component ---- */

export function AuditView({ entityType }: { entityType: string }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  /* ---- Local state ---- */
  const [lookbackDays, setLookbackDays] = useState<LookbackDays>(7);
  const [maxRecords, setMaxRecords] = useState('');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [selectedMismatchEntityKey, setSelectedMismatchEntityKey] = useState<
    string | null
  >(null);
  const [activeResultTab, setActiveResultTab] = useState<
    'summary' | 'inspect'
  >('summary');
  const [overrideStep, setOverrideStep] = useState<AuditStep | null>(null);

  /* ---- Queries ---- */

  const runsQuery = useQuery({
    queryKey: ['op-sync', 'audit-runs', entityType],
    queryFn: () => listOPSyncAuditRuns(entityType, 20),
    refetchInterval: 5000,
  });

  const selectedRunQuery = useQuery({
    queryKey: ['op-sync', 'audit-run', selectedRunId],
    queryFn: () => getOPSyncAuditRun(selectedRunId!),
    enabled: !!selectedRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.run?.Status;
      return status &&
        ['Detecting', 'Detected', 'Queued', 'Auditing'].includes(status)
        ? 5000
        : false;
    },
  });

  const selectedRunStatus = selectedRunQuery.data?.run?.Status;
  const resultRefetchInterval =
    selectedRunStatus &&
    ['Detecting', 'Queued', 'Auditing'].includes(selectedRunStatus)
      ? 5000
      : false;

  const fieldSummaryQuery = useQuery({
    queryKey: [
      'op-sync',
      'audit-run-field-summary',
      selectedRunId,
      selectedRunStatus,
    ],
    queryFn: () => getOPSyncAuditRunFieldSummary(selectedRunId!),
    enabled: !!selectedRunId,
    refetchInterval: resultRefetchInterval,
  });

  const mismatchQuery = useQuery({
    queryKey: [
      'op-sync',
      'audit-run-results',
      selectedRunId,
      'MISMATCH',
      selectedRunStatus,
    ],
    queryFn: () => getOPSyncAuditRunResults(selectedRunId!, 'MISMATCH', 50, 0),
    enabled: !!selectedRunId && activeResultTab === 'inspect',
    refetchInterval:
      activeResultTab === 'inspect' ? resultRefetchInterval : false,
  });

  const failedQuery = useQuery({
    queryKey: [
      'op-sync',
      'audit-run-results',
      selectedRunId,
      'FAILED',
      selectedRunStatus,
    ],
    queryFn: () => getOPSyncAuditRunResults(selectedRunId!, 'FAILED', 50, 0),
    enabled: !!selectedRunId && activeResultTab === 'summary',
    refetchInterval:
      activeResultTab === 'summary' ? resultRefetchInterval : false,
  });

  const skippedQuery = useQuery({
    queryKey: [
      'op-sync',
      'audit-run-results',
      selectedRunId,
      'SKIPPED',
      selectedRunStatus,
    ],
    queryFn: () => getOPSyncAuditRunResults(selectedRunId!, 'SKIPPED', 50, 0),
    enabled: !!selectedRunId && activeResultTab === 'summary',
    refetchInterval:
      activeResultTab === 'summary' ? resultRefetchInterval : false,
  });

  const detectedQuery = useQuery({
    queryKey: ['op-sync', 'audit-run-results', selectedRunId, 'PENDING'],
    queryFn: () =>
      getOPSyncAuditRunResults(selectedRunId!, 'PENDING', 200, 0),
    enabled: !!selectedRunId && selectedRunStatus === 'Detected',
  });

  /* ---- Mutations ---- */

  const detectMutation = useMutation({
    mutationFn: ({ days, max }: { days: number; max?: number }) =>
      startOPSyncAuditDetectOnly(entityType, days, max),
    onSuccess: (data) => {
      setSelectedRunId(data.auditRunId);
      setSelectedKeys(new Set());
      setOverrideStep(null);
      toast({
        title: 'Detection started',
        description: 'Scanning for changed records…',
      });
      queryClient.invalidateQueries({ queryKey: ['op-sync'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Detect failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const runAllMutation = useMutation({
    mutationFn: ({ days, max }: { days: number; max?: number }) =>
      startOPSyncAuditWindow(entityType, days, max),
    onSuccess: (data) => {
      setSelectedRunId(data.auditRunId);
      setSelectedKeys(new Set());
      setOverrideStep(null);
      toast({
        title: 'Audit started',
        description: `${data.detection.totalProcessed} changed records detected`,
      });
      queryClient.invalidateQueries({ queryKey: ['op-sync'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Audit failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const dispatchMutation = useMutation({
    mutationFn: ({
      auditRunId,
      entityKeys,
    }: {
      auditRunId: string;
      entityKeys: string[];
    }) => dispatchOPSyncAuditRun(auditRunId, entityKeys),
    onSuccess: (data) => {
      setSelectedKeys(new Set());
      setOverrideStep('results');
      toast({
        title: 'Audit dispatched',
        description: `${data.totalRecords} record${data.totalRecords === 1 ? '' : 's'} queued`,
      });
      queryClient.invalidateQueries({ queryKey: ['op-sync'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Dispatch failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  /* ---- Derived state ---- */

  const runs = runsQuery.data?.runs || [];
  const selectedRun =
    selectedRunQuery.data?.run ??
    runs.find((r) => r.AuditRunId === selectedRunId) ??
    null;
  const fieldSummary = fieldSummaryQuery.data?.summary || [];
  const detectedRecords = detectedQuery.data?.records || [];

  const totals = selectedRun
    ? {
        changed: selectedRun.DetectionTotal,
        inSync: selectedRun.InSyncCount,
        mismatched: selectedRun.MismatchCount,
        missing: selectedRun.MissingCount,
        failed: selectedRun.FailedCount,
      }
    : { changed: 0, inSync: 0, mismatched: 0, missing: 0, failed: 0 };

  const parsedMax = maxRecords ? parseInt(maxRecords, 10) : undefined;
  const sinceDate = buildSinceDate(lookbackDays);
  const isBusy =
    detectMutation.isPending ||
    runAllMutation.isPending ||
    dispatchMutation.isPending;

  /* ---- Step logic ---- */

  const naturalStep: AuditStep = (() => {
    if (!selectedRun) return 'configure';
    if (selectedRun.Status === 'Detecting') return 'configure';
    if (selectedRun.Status === 'Detected') return 'select';
    return 'results';
  })();

  const step = overrideStep ?? naturalStep;

  const reachableSteps = new Set<AuditStep>(['configure']);
  if (selectedRun) {
    if (selectedRun.Status === 'Detected') reachableSteps.add('select');
    if (
      ['Queued', 'Auditing', 'Completed', 'Failed'].includes(
        selectedRun.Status,
      )
    ) {
      reachableSteps.add('results');
    }
  }

  /* ---- Effects ---- */

  useEffect(() => {
    setSelectedRunId(null);
    setSelectedKeys(new Set());
    setOverrideStep(null);
  }, [entityType]);

  useEffect(() => {
    if (runs.length > 0 && !selectedRunId) {
      setSelectedRunId(runs[0].AuditRunId);
    }
  }, [runs, selectedRunId]);

  useEffect(() => {
    setSelectedKeys(new Set());
    setSelectedMismatchEntityKey(null);
    setOverrideStep(null);
  }, [selectedRunId]);

  useEffect(() => {
    const mismatchRecords = mismatchQuery.data?.records || [];
    if (activeResultTab !== 'inspect') return;
    if (mismatchRecords.length === 0) {
      setSelectedMismatchEntityKey(null);
      return;
    }
    if (
      !selectedMismatchEntityKey ||
      !mismatchRecords.some(
        (r) => r.EntityKey === selectedMismatchEntityKey,
      )
    ) {
      setSelectedMismatchEntityKey(mismatchRecords[0].EntityKey);
    }
  }, [activeResultTab, mismatchQuery.data?.records, selectedMismatchEntityKey]);

  /* ---- Loading copy ---- */

  let loadingTitle = '';
  let loadingDescription = '';
  if (detectMutation.isPending) {
    loadingTitle = 'Detecting changed records';
    loadingDescription = 'Checking Dataverse for changes…';
  } else if (runAllMutation.isPending) {
    loadingTitle = 'Starting audit';
    loadingDescription = 'Creating run and queuing comparison work…';
  } else if (dispatchMutation.isPending) {
    loadingTitle = 'Dispatching records';
    loadingDescription = 'Queuing selected records for audit…';
  } else if (selectedRunStatus === 'Detecting') {
    loadingTitle = 'Detection in progress';
    loadingDescription = 'Finding changed records in Dataverse…';
  } else if (selectedRunStatus === 'Queued') {
    loadingTitle = 'Audit queued';
    loadingDescription = 'Background comparison will start shortly.';
  } else if (selectedRunStatus === 'Auditing') {
    loadingTitle = 'Audit running';
    loadingDescription =
      'Comparing payloads — tables update automatically.';
  }
  const showLoading = !!loadingTitle;

  /* ---- Helpers ---- */

  const runStatusLabel =
    selectedRun?.Status === 'Completed'
      ? 'Audit complete'
      : selectedRun?.Status === 'Failed'
        ? 'Audit failed'
        : 'Audit in progress';

  const runStatusVariant =
    selectedRun?.Status === 'Completed'
      ? 'success'
      : selectedRun?.Status === 'Failed'
        ? 'destructive'
        : ('info' as const);

  /* ---- Render ---- */

  return (
    <div className="space-y-4">
      <StepBreadcrumb
        current={step}
        reachable={reachableSteps}
        onStepClick={setOverrideStep}
      />

      {showLoading && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertTitle>{loadingTitle}</AlertTitle>
          <AlertDescription>{loadingDescription}</AlertDescription>
        </Alert>
      )}

      {/* ──── Step 1: Configure ──── */}
      {step === 'configure' && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">Run an Audit</CardTitle>
              <CardDescription>
                Find records that changed in Dataverse, then compare their
                field values against Open Publishing to spot discrepancies.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-3">
              <div>
                <div className="mb-1.5 text-xs font-medium text-muted-foreground">
                  Detection window
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {LOOKBACK_OPTIONS.map((d) => (
                    <Button
                      key={d}
                      size="sm"
                      variant={lookbackDays === d ? 'default' : 'outline'}
                      onClick={() => setLookbackDays(d)}
                      className="h-8 px-2.5"
                    >
                      {d}d
                    </Button>
                  ))}
                  <div className="mx-1 h-6 w-px bg-border" />
                  <Input
                    type="number"
                    value={maxRecords}
                    onChange={(e) => setMaxRecords(e.target.value)}
                    placeholder="Max records"
                    className="h-8 w-28"
                  />
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground">
                  Records modified since{' '}
                  <code className="font-mono">
                    {sinceDate.slice(0, 10)}
                  </code>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    detectMutation.mutate({
                      days: lookbackDays,
                      max: parsedMax,
                    })
                  }
                  disabled={isBusy}
                  className="h-8"
                >
                  {detectMutation.isPending
                    ? 'Detecting…'
                    : 'Detect Changes'}
                </Button>
                <span className="text-xs text-muted-foreground">or</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    runAllMutation.mutate({
                      days: lookbackDays,
                      max: parsedMax,
                    })
                  }
                  disabled={isBusy}
                  className="h-8"
                >
                  {runAllMutation.isPending
                    ? 'Running…'
                    : 'Detect & Audit All'}
                </Button>
                <span className="text-xs italic text-muted-foreground">
                  Skips selection, audits everything
                </span>
              </div>
            </CardContent>
          </Card>

          <div>
            <h3 className="mb-1 text-sm font-medium">Recent Runs</h3>
            <p className="mb-2 text-xs text-muted-foreground">
              Click a run to view its results.
            </p>
            <AuditRunsTable
              runs={runs}
              isLoading={runsQuery.isLoading}
              selectedRunId={selectedRunId}
              onSelectRun={setSelectedRunId}
            />
          </div>
        </div>
      )}

      {/* ──── Step 2: Select Changes ──── */}
      {step === 'select' && (
        <div className="space-y-4">
          {selectedRun && (
            <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
              <span>
                Detected:{' '}
                <strong className="text-foreground">
                  {selectedRun.DetectionTotal}
                </strong>{' '}
                records
              </span>
              <span>
                Run:{' '}
                <span className="font-mono text-foreground">
                  {selectedRun.AuditRunId.slice(0, 8)}
                </span>
              </span>
              <span>{formatDateTime(selectedRun.StartedAt)}</span>
              <Badge variant="warning" className="text-[10px]">
                {selectedRun.Status}
              </Badge>
            </div>
          )}

          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-base">
                Select Records to Audit
              </CardTitle>
              <CardDescription>
                These records changed in Dataverse. Select which ones to
                compare against Open Publishing.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 p-4 pt-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  <strong>{detectedQuery.data?.total || 0}</strong>{' '}
                  <span className="text-muted-foreground">
                    record
                    {(detectedQuery.data?.total || 0) === 1 ? '' : 's'}{' '}
                    pending audit
                  </span>
                </div>
                <Button
                  size="sm"
                  disabled={
                    selectedKeys.size === 0 ||
                    dispatchMutation.isPending ||
                    !selectedRunId
                  }
                  onClick={() => {
                    if (!selectedRunId) return;
                    dispatchMutation.mutate({
                      auditRunId: selectedRunId,
                      entityKeys: Array.from(selectedKeys),
                    });
                  }}
                >
                  {dispatchMutation.isPending
                    ? 'Dispatching…'
                    : `Audit Selected (${selectedKeys.size})`}
                </Button>
              </div>
              <DetectedChangesTable
                records={detectedRecords}
                isLoading={detectedQuery.isLoading}
                selectedKeys={selectedKeys}
                onSelectionChange={setSelectedKeys}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ──── Step 3: Results ──── */}
      {step === 'results' && (
        <div className="space-y-4">
          {/* Results header card — narrative + metrics in one place */}
          {selectedRun && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {runStatusLabel}
                      </span>
                      <Badge
                        variant={runStatusVariant}
                        className="text-[10px]"
                      >
                        {selectedRun.Status}
                      </Badge>
                    </div>
                    {selectedRun.Status === 'Completed' && (
                      <p className="text-sm text-muted-foreground">
                        {totals.mismatched > 0 ? (
                          <>
                            <span className="font-medium text-destructive">
                              {totals.mismatched}
                            </span>{' '}
                            of {totals.changed} record
                            {totals.changed !== 1 ? 's' : ''} have field
                            differences between Dataverse and Open
                            Publishing
                          </>
                        ) : totals.changed > 0 ? (
                          <span className="text-emerald-600">
                            All {totals.changed} checked record
                            {totals.changed !== 1 ? 's' : ''} are in sync
                          </span>
                        ) : (
                          'No records were checked'
                        )}
                      </p>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 shrink-0 gap-1.5 text-xs"
                    onClick={() => setOverrideStep('configure')}
                  >
                    <RotateCcw className="h-3 w-3" />
                    New Audit
                  </Button>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 border-t pt-3">
                  <Stat label="Changed" value={totals.changed} />
                  <Stat
                    label="In Sync"
                    value={totals.inSync}
                    className="text-emerald-600"
                  />
                  <Stat
                    label="Mismatched"
                    value={totals.mismatched}
                    className="text-destructive"
                  />
                  <Stat
                    label="Missing"
                    value={totals.missing}
                    className="text-amber-600"
                  />
                  <Stat
                    label="Failed"
                    value={totals.failed}
                    className="text-destructive"
                  />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  Run {selectedRun.AuditRunId.slice(0, 8)} ·{' '}
                  {formatDateTime(selectedRun.StartedAt)}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Two views of the results */}
          <Tabs
            value={activeResultTab}
            onValueChange={(v) =>
              setActiveResultTab(v as 'summary' | 'inspect')
            }
          >
            <TabsList>
              <TabsTrigger value="summary">By Field</TabsTrigger>
              <TabsTrigger value="inspect">
                By Record
                {totals.mismatched > 0 && (
                  <Badge
                    variant="destructive"
                    className="ml-1.5 h-4 min-w-4 px-1 text-[10px]"
                  >
                    {totals.mismatched}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* ── By Field: aggregate mismatch breakdown ── */}
            <TabsContent value="summary">
              <div className="space-y-6">
                {showLoading && fieldSummary.length === 0 && (
                  <Card>
                    <CardContent className="space-y-4 p-4">
                      <div className="space-y-2">
                        <Skeleton className="h-6 w-40" />
                        <Skeleton className="h-4 w-96 max-w-full" />
                      </div>
                      <div className="space-y-3">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {fieldSummary.length > 0 && (
                  <Card>
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="text-base">
                        Field Mismatches
                      </CardTitle>
                      <CardDescription>
                        Which fields differ between Dataverse and Open
                        Publishing, sorted by severity. Sample values are
                        from one representative record.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <FieldMismatchTable data={fieldSummary} />
                    </CardContent>
                  </Card>
                )}

                <section>
                  <h4 className="mb-2 text-sm font-medium">
                    Failed Records
                    {totals.failed > 0 && (
                      <Badge
                        variant="destructive"
                        className="ml-2 text-[10px]"
                      >
                        {totals.failed}
                      </Badge>
                    )}
                  </h4>
                  <AuditRunItemsTable
                    records={failedQuery.data?.records || []}
                    isLoading={failedQuery.isLoading}
                    emptyMessage="No failures — all records were compared successfully"
                    showError
                  />
                </section>

                <section>
                  <h4 className="mb-2 text-sm font-medium">
                    Skipped Records
                  </h4>
                  <AuditRunItemsTable
                    records={skippedQuery.data?.records || []}
                    isLoading={skippedQuery.isLoading}
                    emptyMessage="No records were skipped"
                    showSkipReason
                  />
                </section>
              </div>
            </TabsContent>

            {/* ── By Record: field-by-field comparison for each record ── */}
            <TabsContent value="inspect">
              <AuditRecordReviewWorkspace
                records={mismatchQuery.data?.records || []}
                total={mismatchQuery.data?.total || 0}
                isLoading={mismatchQuery.isLoading}
                selectedRecordKey={selectedMismatchEntityKey}
                onSelectRecord={setSelectedMismatchEntityKey}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
